import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable no-console */
import React from 'react';
import { BrowserClient, Hub, Severity } from '@sentry/browser';
import { FormattedMessage } from 'react-intl-next';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import { N0, R400 } from '@atlaskit/theme/colors';
import { fireOperationalAnalytics } from '@atlassian/analytics-bridge';
import { isFedRamp } from '@atlassian/atl-context';
import { maskEmailIds } from '@atlassian/proforma-utils';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { Notices } from '../../components/Notice/noticeTypes';
import { Environment } from '../../models';
// structured tags that we send to Sentry
// everything else from details object will go into "extras"
var TAGS = ['module', 'actionSubject', 'url'];
// From https://sentry.io/settings/atlassian-2y/projects/proforma/keys/
var SENTRY_PUBLIC_KEY = 'c6ac6cc7ef554e3286ec09d412c9c1f4';
var SENTRY_PROJECT_ID = '6388218';
var SENTRY_COMMERCIAL_DSN = "https://".concat(SENTRY_PUBLIC_KEY, "@o55978.ingest.sentry.io/").concat(SENTRY_PROJECT_ID);
var SENTRY_FEDRAMP_DSN = "https://".concat(SENTRY_PUBLIC_KEY, "@sentry.atlassian-us-gov-mod.com/").concat(SENTRY_PROJECT_ID);
var PROFORMA_SENTRY_PROJECT_DSN = isFedRamp() ? SENTRY_FEDRAMP_DSN : SENTRY_COMMERCIAL_DSN;
export var LiveErrorUtils = /*#__PURE__*/function () {
  function LiveErrorUtils(moduleName, showFlag, createAnalyticsEvent, environment) {
    _classCallCheck(this, LiveErrorUtils);
    this.moduleName = void 0;
    this.showFlag = void 0;
    this.createAnalyticsEvent = void 0;
    this.sentryHub = void 0;
    this.environment = void 0;
    this.moduleName = moduleName;
    this.showFlag = showFlag;
    this.createAnalyticsEvent = createAnalyticsEvent;
    this.environment = environment;
    this.sentryHub = this.getSentryHub(environment);
  }
  return _createClass(LiveErrorUtils, [{
    key: "reportError",
    value: function reportError(error, options) {
      var _options$actionSubjec;
      var errorPrefix = "Proforma(".concat(this.moduleName, "): ");
      var severity = options !== null && options !== void 0 && options.severity ? options.severity : Severity.Error;

      // 1) Log error in console
      switch (severity) {
        case Severity.Warning:
          console.warn(errorPrefix, error);
          break;
        case Severity.Info:
          console.info(errorPrefix, error);
          break;
        default:
          console.error(errorPrefix, error);
          break;
      }
      var actionSubject = (_options$actionSubjec = options === null || options === void 0 ? void 0 : options.actionSubject) !== null && _options$actionSubjec !== void 0 ? _options$actionSubjec : ActionSubject.PROFORMA_UI;
      var attributes = (options === null || options === void 0 ? void 0 : options.attributes) && Object.fromEntries(Object.entries(options.attributes).map(function (_ref) {
        var _ref2 = _slicedToArray(_ref, 2),
          key = _ref2[0],
          value = _ref2[1];
        return [key, typeof value === 'string' ? maskEmailIds(value) : value];
      }));

      // Todo: add user agent, package version, url,
      var details = _objectSpread({
        actionSubject: actionSubject,
        errorId: options === null || options === void 0 ? void 0 : options.id,
        severity: severity,
        module: this.moduleName,
        errorMsg: error.message,
        errorInfo: options === null || options === void 0 ? void 0 : options.errorInfo,
        isClientFetchError: error.isClientFetchError,
        requestName: error.requestName,
        traceId: error.traceId,
        status: error.status,
        userAgent: this.getBrowserUserAgent(),
        url: this.getUrl()
      }, attributes);

      // 2) Send error to Atlassian analytics pipeline (GasV3)
      fireOperationalAnalytics(this.createAnalyticsEvent({}), "".concat(actionSubject, " ").concat(Action.FAILED), details);

      // 3) Send error to Sentry
      if (this.environment === Environment.LOCAL || this.sentryHub === undefined) {
        return;
      }
      var sentryHub = this.sentryHub;
      sentryHub.withScope(function (scope) {
        scope.setLevel(severity);
        Object.keys(details).forEach(function (key) {
          var value = details[key];
          if (value) {
            if (TAGS.includes(key)) {
              scope.setTag(key, value.toString());
            } else {
              scope.setExtra(key, value);
            }
          }
        });
        sentryHub.captureException(error);
      });
    }

    /**
     * @private
     * @deprecated notifyUser has been deprecated. Please use the `useFlags()` hook directly instead.
     */
  }, {
    key: "notifyUser",
    value: function notifyUser(errorNotice, customDescription) {
      var notice = Notices[errorNotice];
      if (notice) {
        this.showFlag({
          appearance: 'error',
          isAutoDismiss: true,
          //TO DO - https://product-fabric.atlassian.net/browse/DSP-21423
          // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
          icon: /*#__PURE__*/React.createElement(ErrorIcon, {
            label: "",
            LEGACY_secondaryColor: "var(--ds-icon-danger, ".concat(R400, ")"),
            color: "var(--ds-icon-inverse, ".concat(N0, ")")
          }),
          title: /*#__PURE__*/React.createElement(FormattedMessage, notice.title),
          description: customDescription ? customDescription : /*#__PURE__*/React.createElement(FormattedMessage, notice.message)
        });
        return Promise.resolve();
      } else {
        console.error("Could not show error, notice type ".concat(errorNotice, " does not exist"));
        return Promise.reject();
      }
    }
  }, {
    key: "getSentryHub",
    value: function getSentryHub(environment) {
      try {
        var client = new BrowserClient({
          environment: environment,
          dsn: PROFORMA_SENTRY_PROJECT_DSN
        });
        return new Hub(client);
      } catch (error) {
        var actionSubject = 'getProformaSentryHub';
        var details = {
          actionSubject: actionSubject,
          severity: Severity.Error,
          module: this.moduleName,
          errorMsg: error.message,
          userAgent: this.getBrowserUserAgent(),
          url: this.getUrl()
        };
        fireOperationalAnalytics(this.createAnalyticsEvent({}), "".concat(actionSubject, " ").concat(Action.FAILED), details);
      }
    }
  }, {
    key: "getBrowserUserAgent",
    value: function getBrowserUserAgent() {
      var _window$navigator$use, _window;
      return (_window$navigator$use = (_window = window) === null || _window === void 0 || (_window = _window.navigator) === null || _window === void 0 ? void 0 : _window.userAgent) !== null && _window$navigator$use !== void 0 ? _window$navigator$use : 'unknown';
    }
  }, {
    key: "getUrl",
    value: function getUrl() {
      var _window$document$loca, _window2;
      return (_window$document$loca = (_window2 = window) === null || _window2 === void 0 || (_window2 = _window2.document) === null || _window2 === void 0 || (_window2 = _window2.location) === null || _window2 === void 0 ? void 0 : _window2.host) !== null && _window$document$loca !== void 0 ? _window$document$loca : 'unknown';
    }
  }]);
}();